import React, { useState } from 'react';
import { RefreshCw } from 'lucide-react';


const CATEGORIES = [
  'None',
  'Animals',
  'Advertising',
  'Art',
  'Authors',
  'Business',
  'Celebrities',
  'Fictional Characters',
  'Food & Drink',
  'Fruits & Vegetables',
  'Games',
  'Geography',
  'Heroes & Villains',
  'History',
  'Holidays',
  'Inventions',
  'Languages',
  'Literature',
  'Movies',
  'Music',
  'Mythology',
  'Plants',
  'Politics',
  'Pop Culture',
  'Science',
  'Scientists',
  'Sports',
  'Technology',
  'Unusual Facts',
  'Weather'
];

export default function AIQuestionGenerator() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form state
  const [questions, setQuestions] = useState(
    Array(15).fill(null).map(() => ({ 
      category: 'None', 
      specificity: '', 
      targetAnswer: '' 
    }))
  );
  const [visualRound, setVisualRound] = useState({ prompt: '' });
  const [finalQuestion, setFinalQuestion] = useState({ 
    category: 'None', 
    specificity: '' 
  });
  
  const [duplicates, setDuplicates] = useState([]);
  const [duplicateCheckDone, setDuplicateCheckDone] = useState(false);
  
  // Generated results
  const [generatedQuestions, setGeneratedQuestions] = useState(null);
  const [editableQuestions, setEditableQuestions] = useState(null);

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };

  const updateFinalQuestion = (field, value) => {
    setFinalQuestion(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/generate-questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('quizzler_admin_token')}`
        },
        body: JSON.stringify({
          questions,
          visualRound,
          finalQuestion
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate questions');
      }

      const data = await response.json();
      setGeneratedQuestions(data.questions);
      setEditableQuestions(JSON.parse(JSON.stringify(data.questions))); // Deep copy for editing
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
  if (!editableQuestions) return;
  
  // Check for missing visual round URL
  if (editableQuestions.visualRound && !editableQuestions.visualRound.imageUrl) {
    const proceed = window.confirm(
      '⚠️ Warning: No Visual Round Image URL!\n\n' +
      'You haven\'t added an image URL for the visual round. ' +
      'Players won\'t be able to see the visual question without it.\n\n' +
      'Click OK to save anyway, or Cancel to go back and add the URL.'
    );
    if (!proceed) return;
  }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/save-generated-game`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('quizzler_admin_token')}`
        },
        body: JSON.stringify({
          questions: editableQuestions
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save game');
      }

      const data = await response.json();
      alert(`Game saved successfully as: ${data.filename}`);
      
      // Reset form
      setGeneratedQuestions(null);
      setEditableQuestions(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
 const handleCheckDuplicates = async () => {
    if (!editableQuestions) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/check-duplicates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('quizzler_admin_token')}`
        },
        body: JSON.stringify({
          questions: editableQuestions
        })
      });

      if (!response.ok) {
        throw new Error('Failed to check duplicates');
      }

      const data = await response.json();
      setDuplicates(data.duplicates || []);
      setDuplicateCheckDone(true);
      
      if (data.duplicates.length === 0) {
        alert('✅ No duplicates found! Safe to save.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }; 
  
 const handleRegenerateQuestion = async (index, category, specificity, targetAnswer) => {
  setLoading(true);
  
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/regenerate-single-question`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('quizzler_admin_token')}`
      },
      body: JSON.stringify({
        category,
        specificity,
        targetAnswer,
        questionType: 'regular'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to regenerate question');
    }

    const data = await response.json();
    
    // Update the specific question
    const updated = { ...editableQuestions };
    updated.regularQuestions[index] = data.question;
    setEditableQuestions(updated);
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

const handleRegenerateFinalQuestion = async (category, specificity) => {
  setLoading(true);
  
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/regenerate-single-question`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('quizzler_admin_token')}`
      },
      body: JSON.stringify({
        category,
        specificity,
        questionType: 'final'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to regenerate final question');
    }

    const data = await response.json();
    
    // Update the final question
    const updated = { ...editableQuestions };
    updated.finalQuestion = data.question;
    setEditableQuestions(updated);
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

  const updateEditableQuestion = (index, field, value) => {
    const updated = { ...editableQuestions };
    updated.regularQuestions[index][field] = value;
    setEditableQuestions(updated);
  };

  const updateEditableVisualAnswer = (index, value) => {
    const updated = { ...editableQuestions };
    updated.visualRound.answers[index] = value;
    setEditableQuestions(updated);
  };

  const updateEditableFinalQuestion = (field, value) => {
    const updated = { ...editableQuestions };
    updated.finalQuestion[field] = value;
    setEditableQuestions(updated);
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">AI Question Generator</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {!generatedQuestions ? (
        <>
          {/* Input Form */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Regular Questions (15)</h2>
            <div className="space-y-3">
              {questions.map((q, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-1 font-semibold text-gray-600">
                    Q{idx + 1}
                  </div>
                  <select
                    className="col-span-4 border rounded px-3 py-2"
                    value={q.category}
                    onChange={(e) => updateQuestion(idx, 'category', e.target.value)}
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    className="col-span-4 border rounded px-3 py-2"
                    placeholder="Specificity (e.g., 80s, European, etc.)"
                    value={q.specificity}
                    onChange={(e) => updateQuestion(idx, 'specificity', e.target.value)}
                  />
                  <input
 					 type="text"
  					 className="col-span-3 border rounded px-3 py-2"
 					 placeholder="Target Answer (optional)"
					 value={q.targetAnswer || ''}
					 onChange={(e) => updateQuestion(idx, 'targetAnswer', e.target.value)}
				  />
                </div>
              ))}
            </div>
          </div>

 {/* Visual Round */}
<div className="bg-white rounded-lg shadow p-6 mb-6">
  <h2 className="text-xl font-semibold mb-4">Visual Round</h2>
  <input
    type="text"
    className="w-full border rounded px-3 py-2"
    placeholder="Enter visual round prompt (e.g., 'Iconic Movie Posters', 'Famous Logos', etc.)"
    value={visualRound.prompt}
    onChange={(e) => setVisualRound({ ...visualRound, prompt: e.target.value })}
  />
  <p className="text-sm text-gray-500 mt-2">
    Claude will generate 6 answers based on this prompt. You'll create the images after generation.
  </p>
</div>

          {/* Final Question */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Final Question</h2>
            <div className="grid grid-cols-2 gap-4">
              <select
                className="border rounded px-3 py-2"
                value={finalQuestion.category}
                onChange={(e) => updateFinalQuestion('category', e.target.value)}
              >
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <input
                type="text"
                className="border rounded px-3 py-2"
                placeholder="Specificity (optional)"
                value={finalQuestion.specificity}
                onChange={(e) => updateFinalQuestion('specificity', e.target.value)}
              />
            </div>
            <p className="text-sm text-gray-500 mt-2">
              The final question will be more complex and challenging than regular questions.
            </p>
          </div>

          {/* Generate Button */}
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Generating Questions...' : 'Generate Questions with AI'}
          </button>
        </>
      ) : (
        <>
          {/* Edit Generated Questions */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Generated Questions (Edit as needed)</h2>
            
            {/* Regular Questions */}
<div className="space-y-4 mb-6">
  <h3 className="font-semibold text-lg">Regular Questions</h3>
  {editableQuestions.regularQuestions.map((q, idx) => (
    <div key={idx} className="border rounded p-4">
      <div className="flex justify-between items-start mb-2">
        <span className="font-semibold">Question {idx + 1}</span>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{q.category}</span>
          <button
            onClick={() => handleRegenerateQuestion(idx, q.category, questions[idx].specificity, questions[idx].targetAnswer)}
            className="text-teal-600 hover:text-teal-700 text-xl"
            title="Regenerate this question"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>
      <textarea
  className="w-full border rounded px-3 py-2 mb-2 min-h-[80px] resize-y"
  value={q.question}
  onChange={(e) => updateEditableQuestion(idx, 'question', e.target.value)}
/>
      <input
        type="text"
        className="w-full border rounded px-3 py-2"
        placeholder="Answer"
        value={q.answer}
        onChange={(e) => updateEditableQuestion(idx, 'answer', e.target.value)}
      />
    </div>
  ))}
</div>
            {/* Visual Round */}
{editableQuestions.visualRound && (
  <div className="mb-6">
    <h3 className="font-semibold text-lg mb-2">Visual Round</h3>
    <input
  type="text"
  className="w-full border rounded px-3 py-2 mb-3"
  placeholder="Visual round question/instruction"
  value={editableQuestions.visualRound.description || ''}
  onChange={(e) => {
    const updated = { ...editableQuestions };
    updated.visualRound.description = e.target.value;
    setEditableQuestions(updated);
  }}
/>
    
    {/* Visual Builder Link */}
    
      <a href="https://quizzlertrivia.com/gridbuild"
      target="_blank"
      rel="noopener noreferrer"
      className="inline-block bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 transition mb-3"
    >
      Visual Round Image Builder
    </a>
    
    {/* Answers Grid */}
    <div className="grid grid-cols-2 gap-3 mb-3">
      {editableQuestions.visualRound.answers.map((answer, idx) => (
  <div key={idx} className="flex items-center gap-2">
    <span className="font-bold text-teal-600 text-lg w-6">{idx + 1}.</span>
    <input
      type="text"
      className="flex-1 border rounded px-3 py-2"
      placeholder={`Answer ${idx + 1}`}
      value={answer}
      onChange={(e) => updateEditableVisualAnswer(idx, e.target.value)}
    />
  </div>
))}
    </div>
    
    {/* Image URL Field */}
    <input
      type="text"
      className="w-full border rounded px-3 py-2"
      placeholder="Paste Visual Round Image URL here (after creating it in builder above)"
      value={editableQuestions.visualRound.imageUrl || ''}
      onChange={(e) => {
        const updated = { ...editableQuestions };
        updated.visualRound.imageUrl = e.target.value;
        setEditableQuestions(updated);
      }}
    />
  </div>
)}

            {/* Final Question */}
{editableQuestions.finalQuestion && (
  <div>
    <h3 className="font-semibold text-lg mb-2">Final Question</h3>
    <div className="border rounded p-4">
        <div className="flex justify-between items-start mb-2">
  <span className="text-sm text-gray-500">{editableQuestions.finalQuestion.category}</span>
  <button
    onClick={() => handleRegenerateFinalQuestion(editableQuestions.finalQuestion.category, finalQuestion.specificity)}
    className="text-teal-600 hover:text-teal-700 text-xl"
    title="Regenerate final question"
  >
    <RefreshCw size={18} />
  </button>
</div>
      <input
        type="text"
        className="w-full border rounded px-3 py-2 mb-2"
        value={editableQuestions.finalQuestion.question}
        onChange={(e) => updateEditableFinalQuestion('question', e.target.value)}
      />
      <input
        type="text"
        className="w-full border rounded px-3 py-2"
        placeholder="Answer"
        value={editableQuestions.finalQuestion.answer}
        onChange={(e) => updateEditableFinalQuestion('answer', e.target.value)}
      />
    </div>
  </div>
)}
</div>

          {/* Duplicate Warnings */}
          {duplicates.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-4">
              <h3 className="text-yellow-800 font-bold mb-2">⚠️ Potential Duplicates Found ({duplicates.length})</h3>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {duplicates.map((dup, idx) => (
                  <div key={idx} className="bg-white p-3 rounded border border-yellow-200">
                    <p className="text-sm font-semibold text-gray-700">
                      Q{dup.newQuestion.index} ({dup.matchType}):
                    </p>
                    <p className="text-sm text-gray-600">"{dup.newQuestion.question}"</p>
                    <p className="text-sm text-gray-500">Answer: {dup.newQuestion.answer}</p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Similar to question in game: "{dup.existingQuestion.gameTitle}"
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-yellow-700 mt-2">
                Consider regenerating these questions or proceed anyway.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={handleCheckDuplicates}
              disabled={loading}
              className="flex-1 bg-yellow-500 text-white py-3 rounded-lg font-semibold hover:bg-yellow-600 disabled:bg-gray-400"
            >
              {loading ? 'Checking...' : '🔍 Check for Duplicates'}
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className={`flex-1 py-3 rounded-lg font-semibold ${
                duplicateCheckDone && duplicates.length === 0 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-gray-500 hover:bg-gray-600 text-white'
              } disabled:bg-gray-400`}
            >
              {loading ? 'Saving...' : 'Save to Question Library'}
            </button>
            <button
              onClick={() => {
                setGeneratedQuestions(null);
                setEditableQuestions(null);
                setDuplicates([]);
                setDuplicateCheckDone(false);
              }}
              className="bg-gray-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-gray-700"
            >
              Start Over
            </button>
          </div>
        </>
      )}
    </div>
  );
}